import { matchPath } from 'react-router-dom'
import {
  compact,
  filter,
  find,
  first,
  flatMap,
  groupBy,
  isEmpty,
  isNull,
  isUndefined,
  keys,
  size,
  uniqBy,
  uniqueId
} from 'lodash'
import { saveAs } from 'file-saver'
import memoize from 'moize'
import { addNotification } from 'components/Notifications/actions'
import { openModal } from 'components/Modal/actions'
import { ExportApi } from 'api/export'
import { ImportApi } from 'api/import'
import {
  ImportTranslationsApi,
  ExportTranslationsApi,
  ResultTranslationApi
} from 'api/translations'
import {
  canCreate,
  canImport,
  canExport,
  getPermissionsValue,
  allPermissions
} from 'services/auth'
import { appConfigStore, modalStore, sidebarStore } from 'services/state'
import { createFilterbarApiActions } from 'components/Filterbar/actions'
import { EXTRA_OUTPUT } from 'src/App/constants'
import { updateConfig } from 'services/api/config'
import { XLS } from 'components/ExportModal/constants'

export const mergePaths = (...paths) => {
  return '/'.concat(
    paths
      .reduce((acc, inc) => [...acc, ...(inc || '').split('/')], [])
      .filter(Boolean)
      .join('/')
  )
}

const getDefaultRoute = (routes) => {
  return routes.find((route) => !route.path && route.public)
}

export const getCurrentRoute = (pathname, params) => {
  const { flatRoutes, routes } = params
  const currentRoute = flatRoutes.find((route) => {
    if (!route || !route.path) return false
    return matchPath(pathname, {
      path: route.path,
      exact: true
    })
  })

  return currentRoute || getDefaultRoute(routes)
}

export const inheritParentRouteProps = memoize((routes, parent) => {
  return routes.reduce((acc, inc) => {
    const routePath = inc.path && mergePaths(parent && parent.path, inc.path)

    const proxyParent = parent || {}
    const inheritedProps = {
      shell: proxyParent.shell,
      roles: proxyParent.roles,
      public: proxyParent.public
    }

    const route = { ...inheritedProps, ...inc, path: routePath, parent }
    const routeRoutes = inc.routes && inheritParentRouteProps(inc.routes, route)

    return [
      ...acc,
      {
        ...route,
        routes: routeRoutes
      }
    ]
  }, [])
})

export const flattenRoutes = (routes) =>
  (routes || []).reduce((acc, inc) => {
    return [...acc, inc, ...flattenRoutes(inc.routes)]
  }, [])

export const matchPathRecursive = memoize((pathname, route) => {
  if (!route) {
    return false
  }

  if (matchPath(pathname, { path: route.path, exact: true })) {
    return true
  }

  if (route.routes) {
    return route.routes.some((childRoute) =>
      matchPathRecursive(pathname, childRoute)
    )
  }

  return false
})

export const mapRoutesToHeaders = (routes, t) => {
  return routes
    .filter((route) => route.header)
    .map(({ routes, ...rest }) => {
      const items = routes ? mapRoutesToHeaders(routes, t) : []
      return {
        ...rest,
        title: t(rest.title),
        items: items.length > 0 ? items : undefined
      }
    })
}

const getSidebarTranslationsActions = (groupName, t) => [
  {
    title: t('Translations:Import'),
    onClick: () => {
      openModal('import', {
        onSubmit: (file) => ImportTranslationsApi.importTranslation(file)
      })
    }
  },
  {
    title: t('Export'),
    onClick: async () => {
      addNotification({ message: t('Shell:ExportStarted') })
      const { blob, fileName } = await ExportTranslationsApi.exportTranslation(
        groupName
      )
      saveAs(blob, fileName || `${groupName}.xlsx`)
    }
  },
  {
    title: t('Translations:NewTranslation'),
    onClick: () => {
      openModal('newTranslation', {
        type: 'SUBGROUP',
        group: groupName
      })
    }
  }
]

const onExportWtGroup = (t, idInstance, groupName, fileFormat) => {
  ExportApi.exportWorkingTableGroup({
    startedNotificationText: t('Shell:ExportStarted'),
    pendingNotificationText: t('Shell:ExportPending'),
    instanceId: idInstance,
    wtGroup: groupName,
    fileFormat
  })
}

const getSidebarWtActions = (
  idInstance,
  groupName,
  canImportGroup,
  canExportGroup,
  t,
  exportAllowed
) =>
  compact([
    canImportGroup && {
      title: t('Import'),
      onClick: () => {
        openModal('import', {
          onSubmit: (file) =>
            ImportApi.importWorkingTableGroup({
              file,
              instanceId: idInstance,
              wtGroup: groupName,
              startedNotificationText: t('Shell:ImportStarted'),
              pendingNotificationText: t('Shell:ImportPending'),
              t
            })
        })
      }
    },
    canExportGroup && {
      title: t('Export'),
      onClick: () => {
        if (
          isEmpty(exportAllowed) ||
          (exportAllowed && size(exportAllowed) > 1)
        ) {
          openModal('export', {
            exportAllowed,
            onExport: (fileFormat) =>
              onExportWtGroup(t, idInstance, groupName, fileFormat)
          })
        } else {
          onExportWtGroup(
            t,
            idInstance,
            groupName,
            first(exportAllowed) !== XLS && first(exportAllowed)
          )
        }
      }
    }
  ])

export const getSidebarListTables = memoize(
  (items, { type }, instances, currentInstanceId, exportAllowed, t, models) => {
    if (items?.some((item) => item.loading)) return items
    const isTranslations = type === 'translations'
    const isWorkingTables = type === 'workingTables'
    const isMasterData =
      isWorkingTables && isMasterDataInstance(instances, currentInstanceId)

    if (isTranslations || isWorkingTables) {
      const groups = groupBy(items, 'groups')
      const sidebarListItems = Object.values(groups).map((group) => {
        const translatedItems = group.map((item) => ({
          ...item,
          id: isTranslations ? `${item.id}-${item.type}` : item.id,
          title: t(`Sidebar:${item.title || item.type.toUpperCase()}`)
        }))

        // Permissions in model are higher priority than the others,  so we need
        // to check the permissions of each group's wts. If there is at least one permission
        // on a wt, then the whole group will have that permission
        let canImportByModelPerGroup
        let canExportByModelPerGroup

        if (!isEmpty(models) && !isTranslations) {
          const modelPermissionsInGroup = group.map(
            ({ type }) => models[type].permissions ?? {}
          )

          if (!isEmpty(modelPermissionsInGroup)) {
            canImportByModelPerGroup = modelPermissionsInGroup.some(
              (permission) => {
                // If there permissions're not specified in model, it means we have all permissions
                if (isEmpty(permission)) {
                  return canImport(allPermissions)
                } else {
                  return canImport(getPermissionsValue(permission).permission)
                }
              }
            )

            canExportByModelPerGroup = modelPermissionsInGroup.some(
              (permission) => {
                // If there permissions're not specified in model, it means we have all permissions
                if (isEmpty(permission)) {
                  return canExport(
                    getPermissionsValue(allPermissions).permission
                  )
                } else {
                  return canExport(getPermissionsValue(permission).permission)
                }
              }
            )
          }
        }

        const {
          groups: groupName,
          groupTitle,
          id,
          idInstance
        } = first(group) || {}
        // model permission has higher priority
        const canImportGroup = !isUndefined(canImportByModelPerGroup)
          ? canImportByModelPerGroup
          : group.some(({ permission }) => canImport(permission)) // At least 1 wt with write permission
        // model permission has higher priority
        const canExportGroup = !isUndefined(canExportByModelPerGroup)
          ? canExportByModelPerGroup
          : group.some(({ permission }) => canExport(permission)) // At least 1 wt with write permission

        return {
          items: translatedItems,
          id: `${id}-${groupTitle}`,
          name: groupName,
          title: t(`Sidebar:${groupTitle.toUpperCase()}`),
          actions: isTranslations
            ? getSidebarTranslationsActions(groupName, t)
            : getSidebarWtActions(
                idInstance,
                groupName,
                canImportGroup,
                canExportGroup,
                t,
                exportAllowed
              )
        }
      })
      if (isMasterData || !isWorkingTables) {
        return sidebarListItems
      } else {
        // group scenario on top
        const groupScenario = find(sidebarListItems, (wt) =>
          find(wt?.items, (model) => model?.type === 'scenario')
        )

        if (groupScenario?.name) {
          const sidebarListWithScenario = filter(
            sidebarListItems,
            (group) => group?.name !== groupScenario?.name
          )
          if (sidebarListWithScenario) {
            sidebarListWithScenario.unshift(groupScenario)
            return sidebarListWithScenario
          }
        }
        return sidebarListItems
      }
    }

    if (['output', EXTRA_OUTPUT, 'custom'].includes(type)) {
      const sideNavGroup = items.map((item) => {
        if (isUndefined(item.groups)) {
          return {
            href: item.href,
            title: item.title,
            icon: item.icon
          }
        } else {
          const groups = items.filter((el) => el.groupTitle === item.groupTitle)
          const { groupTitle, icon } = first(groups) || {}

          const outputItems = groups.map((item) => ({
            href: item.href,
            icon: 'none',
            id: item.id,
            title: t(`Sidebar:${item.title || item.type.toUpperCase()}`)
          }))
          return {
            items: outputItems,
            icon,
            id: `${groupTitle}`,
            name: groupTitle,
            title: t(`Sidebar:${groupTitle}`)
          }
        }
      })
      return uniqBy(sideNavGroup, 'title')
    }

    return []
  }
)

export const getSidebarTitleProp = memoize(
  ({ items, type, title }, instances, scenario, instanceId, scenarioId, t) => {
    if (!type) {
      return title ?? ''
    }

    if (type === 'workingTables') {
      if (!items?.length) return t('Shell:MASTERDATA')

      const sidebarTitleMap = instances.reduce(
        (acc, { id, title }) => ({ ...acc, [id]: title }),
        {}
      )

      const id = instanceId

      return sidebarTitleMap[id]
    }

    if (type === 'output') {
      if (!items?.length) return undefined

      return scenario?.name || 'Output'
    }

    if (type === 'translations') return t('Translations:Title')

    return title
  }
)

export const getExtraProps = (props, extraProps = []) =>
  extraProps.reduce((acc, propName) => {
    const propValue = props[propName]
    return propValue ? { ...acc, [propName]: propValue } : acc
  }, {})

const setSidebarStore = ({ drag }) => {
  sidebarStore.update((store) => ({
    ...store,
    drag
  }))
}

const updateModelByOrderMap = (newOrderMapByModelList) => {
  const createOrderMapByModelKey = newOrderMapByModelList.reduce(
    (acc, wtObject) => ({ ...acc, [wtObject.type]: wtObject.order }),
    {}
  )

  const models = appConfigStore.get((store) => store.models)

  const updatedModels = keys(models).reduce((acc, modelKey) => {
    const updatedModel = {
      ...models[modelKey],
      order: createOrderMapByModelKey[modelKey]
    }
    return { ...acc, [modelKey]: updatedModel }
  }, {})
  updateConfig(updatedModels, 'model').then((storedModel) => {
    appConfigStore.update((store) => ({
      ...store,
      models: storedModel
    }))
    setSidebarStore(false)
  })
}

// master data instance does not have config prop or config is null
export const isMasterDataInstance = (instanceList, instanceId) => {
  const findInstanceById = find(
    instanceList,
    (el) => el?.id.toString() === instanceId // toString() -> plat1 instanceId is an integer plat2 is a string
  )

  return (
    isUndefined(findInstanceById?.config) || isNull(findInstanceById?.config)
  )
}

const onExportInstance = (t, instanceId, fileFormat) => {
  ExportApi.exportInstance({
    startedNotificationText: t('Shell:ExportStarted'),
    pendingNotificationText: t('Shell:ExportPending'),
    instanceId,
    fileFormat
  })
}

export const getInstanceActions = (
  instanceId,
  permission,
  isSuperuser,
  sidebarList,
  instances,
  dragAndDropMapModel,
  history,
  exportAllowed,
  t
) => {
  if (!instanceId) return

  const isMasterData = isMasterDataInstance(instances, instanceId)
  const urlEditingSidebar = `/instances/${instanceId}/workingtables`

  const create = canCreate(permission)
  const import_ = canImport(permission)
  const export_ = canExport(permission)

  return compact([
    create &&
      isSuperuser && {
        title: t('InitInstance'),
        onClick: () => {
          openModal('import', {
            onSubmit: (file) =>
              ImportApi.importInstance({
                id: instanceId,
                file,
                truncate: true,
                startedNotificationText: t('Shell:ImportStarted'),
                pendingNotificationText: t('Shell:ImportPending'),
                t
              })
          })
        }
      },
    import_ && {
      title: t('UpdateInstance'),
      onClick: () => {
        openModal('import', {
          onSubmit: (file) =>
            ImportApi.importInstance({
              id: instanceId,
              file,
              truncate: false,
              startedNotificationText: t('Shell:ImportStarted'),
              pendingNotificationText: t('Shell:ImportPending'),
              t
            })
        })
      }
    },
    export_ && {
      title: t('Export'),
      onClick: () => {
        if (
          isEmpty(exportAllowed) ||
          (exportAllowed && size(exportAllowed) > 1)
        ) {
          openModal('export', {
            exportAllowed,
            onExport: (fileFormat) =>
              onExportInstance(t, instanceId, fileFormat)
          })
        } else {
          onExportInstance(
            t,
            instanceId,
            first(exportAllowed) !== XLS && first(exportAllowed)
          )
        }
      }
    },
    isMasterData &&
      isSuperuser && {
        title: sidebarList?.drag
          ? t('Shell:SaveSideNav')
          : t('Shell:EditSideNav'),
        onClick: () => {
          if (sidebarList.type === 'workingTables') {
            if (sidebarList.drag) {
              openModal('confirmEditedSave', {
                title: t('Shell:confirm'),
                message: t('Shell:ConfirmSaveSideNav'),
                onConfirm: () => {
                  if (!isEmpty(dragAndDropMapModel)) {
                    const wtsByGroup = dragAndDropMapModel.map(
                      (group) => group.wts
                    )
                    updateModelByOrderMap(flatMap(wtsByGroup))
                  }
                  setSidebarStore({ drag: false })
                  addNotification({
                    id: uniqueId('notif-'),
                    type: 'inline',
                    kind: 'success',
                    title: t('Shell:EditingSidenavOff'),
                    message: t('Shell:MessageEditingSidenavOff')
                  })
                },
                onCancel: () => {
                  setSidebarStore({ drag: false })
                  addNotification({
                    id: uniqueId('notif-'),
                    type: 'inline',
                    kind: 'success',
                    title: t('Shell:EditingSidenavOff'),
                    message: t('Shell:MessageEditingSidenavOff')
                  })
                }
              })
            } else {
              setSidebarStore({ drag: true })
              history.push(urlEditingSidebar)
              addNotification({
                id: uniqueId('notif-'),
                type: 'inline',
                kind: 'success',
                title: t('Shell:EditingSidenavOn'),
                message: t('Shell:MessageEditingSidenavOn')
              })
            }
          }
        }
      }
  ])
}

export const getTranslationsActions = (t) => {
  return [
    {
      title: t('Translations:Import'),
      onClick: () => {
        openModal('import', {
          onSubmit: (file) => ImportTranslationsApi.importTranslation(file)
        })
      }
    },
    {
      title: t('Export'),
      onClick: async () => {
        addNotification({ message: t('Shell:ExportStarted') })
        const { blob, fileName } =
          await ExportTranslationsApi.exportAllTranslation()
        saveAs(blob, fileName || `Translations-export.xlsx`)
      }
    },
    {
      title: t('Translations:NewTranslation'),
      onClick: () => {
        openModal('newTranslation', {
          type: 'GROUP'
        })
      }
    },
    {
      title: t('Translations:Search'),
      onClick: () => {
        openModal('search', {
          onSubmit: (value) => ResultTranslationApi.resultTranslation(value)
        })
      }
    }
  ]
}

// Memoized fetchDefault function
// It changes iff the outputName and the instanceId changes
// With maxSize = 1, it updates every time the page changes
const createFetchDefault = memoize(
  (outputName, instanceId) => {
    const { getFilterList } = createFilterbarApiActions(outputName, instanceId)

    return async (signal) => {
      const { name, sections } = first(await getFilterList(true, signal))

      return { name, sections }
    }
  },
  { maxSize: 1 }
)

const createAsyncFetch = (outputName, instanceId) => {
  if (isEmpty(outputName)) return null

  const { getAsyncFilterSections } = createFilterbarApiActions(
    outputName,
    instanceId
  )

  return async (filterData, changedSection, asyncDeps) => {
    const sections = await getAsyncFilterSections(
      filterData,
      changedSection,
      asyncDeps
    )
    return sections
  }
}

// In this point, the platform checks if the filterbar contains the props for the backend
// If it is the case, it transforms the props, passing the functions to be used in order to
// open the modals for the list and the save
export const getFilterProps = ({ servicesConfig, ...props }) => {
  if (isEmpty(servicesConfig)) {
    return props
  }

  const {
    outputName,
    modals = {},
    instanceId,
    translationNamespace = ''
  } = servicesConfig

  const { list = 'filterList', save = 'filterSave' } = modals

  const saveFilter = (data) => {
    const { activeModals } = modalStore.get()

    if (!activeModals.includes(save)) {
      openModal(save, { data, outputName })
    }
  }

  const filterList = (filterNameRendered) => {
    const { activeModals } = modalStore.get()

    if (!activeModals.includes(list)) {
      openModal(list, { outputName, instanceId, filterNameRendered })
    }
  }

  return {
    ...props,
    fetchAsyncSections: createAsyncFetch(outputName, instanceId),
    servicesConfig: {
      saveFilter,
      filterList,
      translationNamespace,
      fetchDefault: createFetchDefault(outputName, instanceId)
    }
  }
}

export const getUserInfo = (user) => {
  if (isEmpty(user)) return { name: '', email: '', roles: [] }

  const email = user.email || ''

  return {
    name: creteName(user),
    email,
    roles: user.roles?.join(', ')
  }
}
const creteName = (user) => {
  const name = user.given_name
  const surname = user.family_name

  if (isUndefined(name) || isUndefined(surname)) {
    if (!isUndefined(user.preferred_username)) {
      return user.preferred_username
    } else {
      return user.sub
    }
  } else return `${name} ${surname}`
}
